import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApolloCache, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';

import { useToast } from '../../../common/hooks/useToast';
import { describeEditError, forgetEditData } from '../contactDetail/edit/contactEdit';
import { CRM_UPDATE_CONTACT_PHONE } from '../contactDetail/edit/contactEdit.queries';
import { CRM_LOG_CALL } from './calls.queries';
import {
  CallField,
  CallForm,
  CallError,
  checkCall,
  chooseOutcome,
  clearDraft,
  describeCallError,
  emptyForm,
  FOLLOW_UP_PRESETS,
  followUpAtOf,
  formatDuration,
  formatFollowUp,
  METHODS,
  NOTES_MAX,
  OUTCOMES,
  outcomeByKey,
  outcomeLabel,
  readDraft,
  SUPPORT_LEVELS,
  toLogCallInput,
  writeDraft,
} from './logCall';
import { RecordingField } from './RecordingField';
import { CallTimer, useCallTimer } from './useCallTimer';

/** What crmLogCall answers. */
export interface LoggedCall {
  id: number;
  contactId: number;
  outcome: string;
  direction: string | null;
  durationSec: number | null;
  occurredAt: string;
  followUpAt: string | null;
  setDoNotCall: boolean;
}

/**
 * After a call, whatever the cache holds about the person's calls is stale:
 * list pages (last contacted, can call), counts, timelines, the call log, and
 * the queue's summary. Forgetting them makes a screen still showing them ask
 * again, once. That includes the sidebar's follow-ups count (`crmCalls`), when
 * it is shown: a refetch by name would warn wherever it is not, as in
 * production while Follow-ups is unbuilt.
 */
export const forgetCallData = (cache: ApolloCache<unknown>) => {
  ['crmContacts', 'crmContactCounts', 'crmContactTimeline', 'crmCalls', 'crmCallQueueSummary'].forEach((fieldName) =>
    cache.evict({ id: 'ROOT_QUERY', fieldName }),
  );
  cache.gc();
};

const STORAGE_MORE = 'ilbp.crm.callForm.more';

const readMoreOpen = () => {
  try {
    return window.localStorage.getItem(STORAGE_MORE) === 'open';
  } catch {
    return false;
  }
};

const storeMoreOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_MORE, open ? 'open' : 'closed');
  } catch {
    // The choice lasts for this form only.
  }
};

/** Typing in a field: number keys are text there, not outcomes. */
const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable || (tag === 'INPUT' && (el as HTMLInputElement).type !== 'checkbox' && (el as HTMLInputElement).type !== 'radio');
};

export interface LogCallFormProps {
  contactId: number;
  /** Their first name or full name: "Did Ada ask not to be called?" */
  name: string;
  /**
   * Marked do not call or do not contact: an outbound call cannot be logged,
   * so the form logs a call they made to us.
   */
  blocked: null | 'doNotCall' | 'doNotContact';
  /** The submit button's words: "Log call", or "Log and next" in the queue. */
  submitLabel: string;
  onLogged: (call: LoggedCall) => void;
  /** Beside submit: Cancel, or the queue's Skip. */
  secondary?: React.ReactNode;
  /** The queue's timer, started when a number is dialled. The form keeps its own otherwise. */
  timer?: CallTimer;
  /** Focus the outcomes when the form appears. */
  autoFocus?: boolean;
  /** Lets the queue's own keys submit the form (Ctrl+Enter anywhere on its screen). */
  submitRef?: React.MutableRefObject<(() => void) | null>;
  /** While the screen around it is busy (the queue fetching the next person). */
  disabled?: boolean;
  /**
   * The number the call went to (card 21.5): with "Wrong number", the form
   * offers to mark it bad, which moves calls to their next good number. Its
   * id is null for an app member's own number, which is not a CRM row yet.
   */
  number?: { id: number | null; text: string } | null;
}

/**
 * Log a call (card 21): the fastest path from hanging up to a recorded call.
 *
 * - **Keys:** 1 to 9 choose what happened and move to the notes; Ctrl+Enter
 *   (⌘+Enter) logs from anywhere in the form.
 * - **A follow-up follows the outcome** until the caller picks one: "No answer"
 *   suggests tomorrow, "Left message" three days.
 * - **The timer** fills in how long the call took; a typed length wins.
 * - **Nothing is lost:** what was typed is kept per person in this browser
 *   until the call is logged, so closing the dialog or reloading is safe.
 * - **Do not call is a hard block,** as in the API: for such a person only a
 *   call they made can be logged.
 * - "They asked not to be called again" marks them do not call on this call.
 * - "Wrong number" offers to mark the number dialled bad (card 21.5).
 *
 * The checks are the API's own (checkCall), made before sending; anything it
 * still refuses is shown against the field it names.
 */
export const LogCallForm = ({
  contactId,
  name,
  blocked,
  submitLabel,
  onLogged,
  secondary,
  timer: outerTimer,
  autoFocus,
  submitRef,
  disabled,
  number,
}: LogCallFormProps) => {
  const ownTimer = useCallTimer();
  const timer = outerTimer ?? ownTimer;

  const initial = useMemo(() => {
    const draft = readDraft(contactId);
    const base = draft ?? emptyForm();
    return { form: blocked ? { ...base, direction: 'INBOUND' as const, doNotCall: false } : base, restored: Boolean(draft) };
    // Once per person: the draft is read when the form opens for them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);
  const [form, setForm] = useState<CallForm>(initial.form);
  const [restored, setRestored] = useState(initial.restored);
  const [moreOpen, setMoreOpen] = useState(
    () => readMoreOpen() || Boolean(initial.form.occurredAt || initial.form.supportLevel || initial.form.recordingUrl),
  );
  const [tried, setTried] = useState(false);
  const [apiError, setApiError] = useState<CallError | null>(null);
  const notesRef = useRef<HTMLTextAreaElement | null>(null);
  const firstOutcome = useRef<HTMLButtonElement | null>(null);

  const [logCall, { loading }] = useMutation<{ crmLogCall: LoggedCall }>(CRM_LOG_CALL, {
    update: (cache) => forgetCallData(cache),
  });
  // Card 21.5: "Wrong number" can mark the number bad, after the call is logged.
  const [markBad, setMarkBad] = useState(true);
  const [markNumber, { loading: marking }] = useMutation(CRM_UPDATE_CONTACT_PHONE, { update: (cache) => forgetEditData(cache) });
  const toast = useToast();

  useEffect(() => {
    if (autoFocus) firstOutcome.current?.focus();
  }, [autoFocus, contactId]);

  const now = dayjs();
  const problems = checkCall(form, now);
  const shown: Partial<Record<CallField, string>> = {
    ...(tried ? problems : { notes: problems.notes, duration: problems.duration, occurredAt: problems.occurredAt, followUp: problems.followUp }),
    ...(apiError?.field ? { [apiError.field]: apiError.message } : {}),
  };
  const followUpAt = followUpAtOf(form, now);
  const busy = loading || marking || Boolean(disabled);
  const offerMarkBad = Boolean(number) && form.outcome === 'BAD_INFO' && form.direction === 'OUTBOUND' && !blocked;

  const update = (next: CallForm) => {
    setForm(next);
    writeDraft(contactId, next);
    setApiError(null);
  };
  const change = (partial: Partial<CallForm>) => update({ ...form, ...partial });

  const pick = (outcome: CallForm['outcome']) => {
    if (!outcome) return;
    update(chooseOutcome(form, outcome));
    // At once, not after a tick: keys typed straight after the number belong in the notes, and a
    // space reaching the outcome button would press it.
    notesRef.current?.focus();
  };

  const submit = async () => {
    if (busy) return;
    setTried(true);
    // A running timer is the call's length, unless the caller typed one.
    let current = form;
    if (timer.running) {
      const seconds = timer.stop();
      if (!form.duration.trim() && seconds > 0) current = { ...form, duration: formatDuration(seconds) };
      setForm(current);
    }
    const at = dayjs();
    const found = checkCall(current, at);
    // A problem under "More details" is shown, not left behind a closed section.
    if (found.occurredAt || found.recordingUrl) setMoreOpen(true);
    if (Object.keys(found).length) return;
    try {
      const { data } = await logCall({ variables: { input: toLogCallInput(contactId, current, at) } });
      if (!data) return;
      clearDraft(contactId);
      timer.reset();
      if (offerMarkBad && markBad && number) {
        try {
          await markNumber({ variables: { contactId, phoneId: number.id, change: { isBad: true } } });
          toast.success(`${number.text} marked bad.`);
        } catch (error) {
          // The call is logged either way; say why the number was not marked.
          toast.warn(`The call is logged, but ${number.text} was not marked bad: ${describeEditError(error as Error).message}`);
        }
      }
      onLogged(data.crmLogCall);
    } catch (error) {
      const refusal = describeCallError(error as Error);
      if (refusal.field === 'occurredAt' || refusal.field === 'recordingUrl') setMoreOpen(true);
      setApiError(refusal);
    }
  };

  // The latest submit, for keys handled outside the form (the queue's Ctrl+Enter).
  const submitLatest = useRef(submit);
  submitLatest.current = submit;
  useEffect(() => {
    if (!submitRef) return undefined;
    submitRef.current = () => submitLatest.current();
    return () => {
      submitRef.current = null;
    };
  }, [submitRef]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      submit();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return;
    const outcome = outcomeByKey(event.key);
    if (outcome) {
      event.preventDefault();
      pick(outcome.value);
    }
  };

  const shownDuration = timer.running ? formatDuration(timer.seconds) : form.duration;
  const followUpChoice = form.followUp;
  const dncName = name || 'they';

  return (
    <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); submit(); }} onKeyDown={onKeyDown} sx={{ display: 'grid', gap: 2 }}>
      {blocked && (
        <Alert severity="error" variant="outlined" icon={<PhoneDisabledOutlinedIcon />} sx={{ py: 0.25 }}>
          Marked {blocked === 'doNotContact' ? 'do not contact' : 'do not call'}. Only a call they made to you can be logged.
        </Alert>
      )}
      {restored && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ py: 0 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                update(blocked ? { ...emptyForm('INBOUND') } : emptyForm());
                clearDraft(contactId);
                setRestored(false);
              }}
            >
              Discard
            </Button>
          }
        >
          Your unsent notes for this call are back.
        </Alert>
      )}

      <Box role="radiogroup" aria-labelledby={`crm-outcome-${contactId}`} aria-describedby={`crm-outcome-keys-${contactId}`}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
          <Typography id={`crm-outcome-${contactId}`} variant="subtitle2" component="p">
            What happened?
          </Typography>
          <Typography id={`crm-outcome-keys-${contactId}`} variant="caption" color="text.secondary">
            Keys 1–9
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
          {OUTCOMES.map((outcome, index) => {
            const on = form.outcome === outcome.value;
            const inboundOnly = Boolean(blocked) && outcome.value === 'INBOUND';
            return (
              <Tooltip key={outcome.value} title={outcome.hint} describeChild enterDelay={600}>
                <Button
                  ref={index === 0 ? firstOutcome : undefined}
                  role="radio"
                  aria-checked={on}
                  variant={on ? 'contained' : 'outlined'}
                  color={on ? 'primary' : 'inherit'}
                  onClick={() => pick(outcome.value)}
                  disabled={busy}
                  sx={(theme) => ({
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: on ? 700 : 500,
                    py: 0.75,
                    px: 0.75,
                    minWidth: 0,
                    fontSize: '0.8125rem',
                    borderColor: on ? undefined : '#dbe2ea',
                    ...(inboundOnly && !on ? { borderColor: alpha(theme.palette.primary.main, 0.5) } : {}),
                  })}
                >
                  <Box
                    component="kbd"
                    aria-hidden
                    sx={{
                      fontFamily: 'inherit',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      lineHeight: '18px',
                      textAlign: 'center',
                      borderRadius: 0.75,
                      mr: 0.625,
                      flexShrink: 0,
                      bgcolor: on ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                      color: on ? 'inherit' : 'text.secondary',
                    }}
                  >
                    {outcome.key}
                  </Box>
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {outcome.label}
                  </Box>
                </Button>
              </Tooltip>
            );
          })}
        </Box>
        {shown.outcome && (
          <Typography variant="caption" color="error" component="p" role="alert" sx={{ mt: 0.5 }}>
            {shown.outcome}
          </Typography>
        )}
      </Box>

      <TextField
        label="Notes"
        placeholder="What did they say? What happens next?"
        value={form.notes}
        onChange={(event) => change({ notes: event.target.value })}
        inputRef={notesRef}
        multiline
        minRows={3}
        maxRows={10}
        disabled={busy}
        error={Boolean(shown.notes)}
        helperText={
          shown.notes ??
          (form.notes.length > NOTES_MAX - 1000 ? `${(NOTES_MAX - form.notes.length).toLocaleString('en-US')} characters left` : 'Ctrl+Enter logs the call')
        }
      />

      {offerMarkBad && number && (
        <FormControlLabel
          sx={{ mt: -1, mr: 0 }}
          control={<Checkbox checked={markBad} onChange={(event) => setMarkBad(event.target.checked)} disabled={busy} />}
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Mark {number.text} bad
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Calls move to their next good number. With none, they leave the call queue.
              </Typography>
            </Box>
          }
        />
      )}

      <Box sx={{ display: 'grid', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" component="p" id={`crm-followup-${contactId}`} sx={{ mr: 0.5 }}>
            Call again
          </Typography>
          <Box role="group" aria-labelledby={`crm-followup-${contactId}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              label="No"
              size="small"
              clickable
              disabled={busy}
              aria-pressed={followUpChoice.kind === 'none'}
              color={followUpChoice.kind === 'none' ? 'primary' : 'default'}
              variant={followUpChoice.kind === 'none' ? 'filled' : 'outlined'}
              onClick={() => change({ followUp: { kind: 'none' } })}
            />
            {FOLLOW_UP_PRESETS.map((preset) => {
              const on = followUpChoice.kind === 'preset' && followUpChoice.id === preset.id;
              return (
                <Chip
                  key={preset.id}
                  label={preset.label}
                  size="small"
                  clickable
                  disabled={busy}
                  aria-pressed={on}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  onClick={() => change({ followUp: { kind: 'preset', id: preset.id, suggested: false } })}
                />
              );
            })}
            <Chip
              icon={<EventOutlinedIcon />}
              label="Pick a date"
              size="small"
              clickable
              disabled={busy}
              aria-pressed={followUpChoice.kind === 'custom'}
              color={followUpChoice.kind === 'custom' ? 'primary' : 'default'}
              variant={followUpChoice.kind === 'custom' ? 'filled' : 'outlined'}
              onClick={() =>
                change({
                  followUp: { kind: 'custom', at: (followUpAt ?? now.add(1, 'day').hour(10).minute(0)).format('YYYY-MM-DDTHH:mm') },
                })
              }
            />
          </Box>
        </Box>
        {followUpChoice.kind === 'custom' && (
          <TextField
            label="Follow up on"
            type="datetime-local"
            size="small"
            value={followUpChoice.at}
            onChange={(event) => change({ followUp: { kind: 'custom', at: event.target.value } })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: now.format('YYYY-MM-DDTHH:mm') }}
            sx={{ maxWidth: 260 }}
            disabled={busy}
          />
        )}
        {(followUpAt?.isValid() || shown.followUp) && (
          <Typography variant="caption" color={shown.followUp ? 'error' : 'text.secondary'} component="p" role={shown.followUp ? 'alert' : undefined}>
            {shown.followUp ??
              `Follow up ${formatFollowUp(followUpAt as dayjs.Dayjs, now)}${
                followUpChoice.kind === 'preset' && followUpChoice.suggested && form.outcome
                  ? `, as usual after "${outcomeLabel(form.outcome)}"`
                  : ''
              }. It comes back in the call queue first, and logging their next call closes it.`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="How long"
          size="small"
          placeholder="4:30"
          value={shownDuration}
          onChange={(event) => change({ duration: event.target.value })}
          disabled={busy}
          error={Boolean(shown.duration)}
          helperText={shown.duration ?? (timer.running ? 'Counting…' : 'Minutes, or 4:30')}
          InputProps={{
            readOnly: timer.running,
            sx: { fontVariantNumeric: 'tabular-nums' },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={timer.running ? 'Stop the timer' : 'Start the timer'}>
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={timer.running ? 'Stop the timer' : 'Start the timer'}
                    disabled={busy}
                    onClick={() => {
                      if (timer.running) {
                        const seconds = timer.stop();
                        change({ duration: seconds > 0 ? formatDuration(seconds) : form.duration });
                      } else {
                        timer.start();
                      }
                    }}
                    color={timer.running ? 'error' : 'default'}
                  >
                    {timer.running ? <StopRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ width: 170 }}
        />
        {!blocked && (
          <FormControlLabel
            sx={{ mt: 0.25, mr: 0 }}
            control={
              <Checkbox
                color="error"
                checked={form.doNotCall}
                onChange={(event) => change({ doNotCall: event.target.checked })}
                disabled={busy}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: form.doNotCall ? 'error.main' : 'text.primary' }}>
                  They asked not to be called again
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Marks {dncName === 'they' ? 'them' : dncName} do not call, on this call.
                </Typography>
              </Box>
            }
          />
        )}
      </Box>

      <Box>
        <Button
          size="small"
          onClick={() => {
            setMoreOpen(!moreOpen);
            storeMoreOpen(!moreOpen);
          }}
          aria-expanded={moreOpen}
          endIcon={<ExpandMoreIcon sx={{ transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
          sx={{ px: 0.5 }}
        >
          {moreOpen ? 'Fewer details' : 'More details'}
          {!moreOpen && (form.direction === 'INBOUND' || form.method !== 'PHONE' || form.occurredAt || form.supportLevel || form.recordingUrl.trim())
            ? ` (${[
                form.direction === 'INBOUND' ? 'inbound' : '',
                form.method !== 'PHONE' ? METHODS.find((m) => m.value === form.method)?.label.toLowerCase() : '',
                form.occurredAt ? 'earlier' : '',
                form.supportLevel ? `support ${form.supportLevel}` : '',
                form.recordingUrl.trim() ? 'recording' : '',
              ]
                .filter(Boolean)
                .join(', ')})`
            : ''}
        </Button>
        <Collapse in={moreOpen} unmountOnExit>
          <Box sx={{ display: 'grid', gap: 2, pt: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={form.direction}
                onChange={(_, value) => value && change({ direction: value })}
                aria-label="Who called"
                disabled={busy}
              >
                <ToggleButton value="OUTBOUND" disabled={Boolean(blocked)} sx={{ textTransform: 'none' }}>
                  We called them
                </ToggleButton>
                <ToggleButton value="INBOUND" sx={{ textTransform: 'none' }}>
                  They called us
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                select
                size="small"
                label="How"
                value={form.method}
                onChange={(event) => change({ method: event.target.value as CallForm['method'] })}
                sx={{ minWidth: 140 }}
                disabled={busy}
              >
                {METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="When"
              type="datetime-local"
              size="small"
              value={form.occurredAt}
              onChange={(event) => change({ occurredAt: event.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: now.format('YYYY-MM-DDTHH:mm') }}
              error={Boolean(shown.occurredAt)}
              helperText={shown.occurredAt ?? (form.occurredAt ? 'Logged after the fact.' : 'Empty: just now.')}
              sx={{ maxWidth: 260 }}
              disabled={busy}
            />
            <Box>
              <Typography variant="subtitle2" component="p" id={`crm-support-${contactId}`} sx={{ mb: 0.5 }}>
                Support level
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={form.supportLevel}
                onChange={(_, value) => change({ supportLevel: value ?? null })}
                aria-labelledby={`crm-support-${contactId}`}
                disabled={busy}
                sx={{ flexWrap: 'wrap' }}
              >
                {SUPPORT_LEVELS.map((level) => (
                  <ToggleButton key={level.value} value={level.value} sx={{ textTransform: 'none', px: 1.25 }}>
                    {level.value} · {level.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                Recorded on the call. It does not change their support level on their record.
              </Typography>
            </Box>
            <RecordingField
              value={form.recordingUrl}
              onChange={(recordingUrl) => change({ recordingUrl })}
              error={shown.recordingUrl}
              disabled={busy}
              onUseDuration={!form.duration.trim() && !timer.running ? (seconds) => change({ duration: formatDuration(seconds) }) : undefined}
            />
          </Box>
        </Collapse>
      </Box>

      {apiError && !apiError.field && (
        <Alert severity={apiError.blocked ? 'warning' : 'error'} role="alert">
          {apiError.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {secondary}
        <Button
          type="submit"
          variant="contained"
          disabled={busy}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minWidth: 140 }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
};
